import { ApiResponse, FormStore, GlobalStore } from "@roc/feature-app-core";
import { DateFormat, floridaCountiesRestriction, formatDate, propertyCitiesRestriction, propertyStates, PURCHASE, REFINANCE } from "@roc/feature-utils";
import { action, computed, flow, makeObservable, observable } from "mobx";
import { isRestrictedCounty } from "../../utils/utils";
import { QuoteService } from "../../services/quoteService";
import { RentalPropertyTypeFormStore } from "./rentalPropertyTypeFormStore";

const FIELD_REQUIRED_ERROR = 'This field is required.';

const initialForm = {
  fields: {
    propertyStates: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    propertyCounties: {
      value: null,
      error: null,
      message: FIELD_REQUIRED_ERROR,
      rule: 'required',
    },
    loanPurpose: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    lastPurchaseDate: {
      value: null,
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }, { valid_date: true }],
      message: FIELD_REQUIRED_ERROR,
    },
    anyDebt: {
      value: null,
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: FIELD_REQUIRED_ERROR,
    },
    totalDebtPayoff: {
      value: null,
      error: null,
      rule: [{ required_if_one_of: ['anyDebt', true] }],
      message: FIELD_REQUIRED_ERROR,
    },
    loanRecourse: {
      value: '',
      error: null,
      message: FIELD_REQUIRED_ERROR,
    },
    acquisitionPrice: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    minimumAsIsValue: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    maximumAsIsValue: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    capitalImprovements: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['loanPurpose', REFINANCE] }],
      message: FIELD_REQUIRED_ERROR,
    },
    totalGrossMonthlyRent: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    totalGrossAnnualTaxes: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    totalGrossAnnualInsurance: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    totalAnnualHOADues: {
      value: '',
      error: null,
      rule: 'required',
      message: FIELD_REQUIRED_ERROR,
    },
    cashOut: {
      value: '',
      error: null,
      rule: [{ required_if_one_of: ['anyDebt', true] }],
      message: FIELD_REQUIRED_ERROR,
    },
    section8: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    key: null,
    editMode: false,
    isValid: false,
    error: null,
  },
};


export class RentalPortfolioInformationFormStore extends FormStore {
  private globalStore: GlobalStore;
  private quoteService: QuoteService;
  public allCounties;
  propertyTypeFormStores: RentalPropertyTypeFormStore[];


  constructor(globalStore) {
    super(initialForm, globalStore);
    this.globalStore = globalStore;
    this.quoteService = new QuoteService();

    makeObservable(this, {
      fetchStateCounties: flow,
      propertyTypeFormStores: observable,
      addNewPropertyType: action,
      deletePropertyType: action,
      totalNoOfProperties: computed,
      rentalPortfoliosPropertyTypesAsString: computed,
      totalEstimatedAsIsValue: computed,
      totalAnnualIncome: computed,
      annualExpenses: computed,
      annualNOI: computed,
      allCounties: observable,
      getCounties: action,
      validateAndCleanupUnusedFields: action,
      getLoanPortfolioInformation: action,
      validateFields: action,
      cleanupUnusedFields: action,
      loadPortfolioInformation: action,
    });
  }

  reset() {
    super.reset();
    this.propertyTypeFormStores = [];
  }

  *fetchStateCounties() {
    try {
      const response: ApiResponse = yield this.quoteService.getStateCounties();
      this.allCounties = response.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting state counties.',
      });
    }
  }

  getProperties() {
    return this.propertyTypeFormStores.map(form => form.getProperty());
  }

  getCounties() {
    if (!this.allCounties) {
      return [];
    }
    const propertyStateId = this.form.fields.propertyStates.value;
    const states = propertyStateId ? [propertyStateId] : [];
    let selectedStateCounties = [];
    if (states && states.length > 0) {
      selectedStateCounties = states.reduce(
        (c, s) => [...c, ...(this.allCounties[s] ?? [])],
        []
      );
    }

    const counties = [];
    if (selectedStateCounties && selectedStateCounties.length > 0) {
      selectedStateCounties.sort().map(county => {
        counties.push({ value: county, label: county });
      });
    }

    return counties;
  }

  addNewPropertyType(initialValues = {}) {
    const newForm = new RentalPropertyTypeFormStore(this.globalStore);
    newForm.loadForm(initialValues);
    this.propertyTypeFormStores.push(newForm);
  }

  deletePropertyType(key) {
    this.propertyTypeFormStores = this.propertyTypeFormStores.filter(store => store.key !== key);
  }

  get totalNoOfProperties() {
    if (!this.propertyTypeFormStores?.length) {
      return 0;
    }

    let res = 0;

    this.propertyTypeFormStores.forEach(store => {
      res += +(store.form.fields.numberOfProperties.value || 0);
    });

    return res;
  }

  get totalEstimatedAsIsValue() {
    if (!this.propertyTypeFormStores?.length) {
      return 0;
    }

    let res = 0;

    this.propertyTypeFormStores.forEach(store => {
      res += +(store.form.fields.estimatedAsIsValue.value || 0);
    });

    return res;
  }

  get totalAnnualIncome() {
    const rent = isNaN(Number(this.form.fields.totalGrossMonthlyRent.value))
      ? 0
      : Number(this.form.fields.totalGrossMonthlyRent.value);
    const totalAnnualIncome = rent * 12;
    return totalAnnualIncome;
  }
  get annualExpenses() {
    const {
      totalGrossAnnualTaxes,
      totalGrossAnnualInsurance,
      totalAnnualHOADues,
    } = this.form.fields;
    const taxes = isNaN(Number(totalGrossAnnualTaxes.value))
      ? 0
      : Number(totalGrossAnnualTaxes.value);
    const insurance = isNaN(Number(totalGrossAnnualInsurance.value))
      ? 0
      : Number(totalGrossAnnualInsurance.value);
    const hoa = isNaN(Number(totalAnnualHOADues.value))
      ? 0
      : Number(totalAnnualHOADues.value);

    const totalAnnualExpenses =
      taxes +
      insurance +
      hoa;

    return totalAnnualExpenses;
  }
  get annualNOI() {
    return this.totalAnnualIncome - this.annualExpenses;
  }

  validateAndCleanupUnusedFields() {
    this.cleanupUnusedFields();
    return this.validateFields();
  }

  validateFields() {
    let isValid = true;

    if (this.propertyTypeFormStores.length === 0) {
      isValid = false;
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please enter at least one property type.',
      });
    }

    this.propertyTypeFormStores.forEach(store => {
      store.runFormValidation();
      isValid = isValid && store.form.meta.isValid;
    });

    this.runFormValidation();
    if (!this.form.fields.propertyCounties.value) {
      this.form.fields.propertyCounties.error = FIELD_REQUIRED_ERROR;
    }

    isValid = isValid && this.form.meta.isValid;

    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields.',
      });
    }

    return isValid;
  }

  cleanupUnusedFields() {
    const isPurchase = this.form.fields.loanPurpose.value === PURCHASE;

    if (isPurchase) {
      this.onFieldChange("lastPurchaseDate", null);
      this.onFieldChange("capitalImprovements", "");
      this.onFieldChange("anyDebt", null);
    }
  }

  onFieldChange(field, value) {
    super.onFieldChange(field, value);

    if (field == 'anyDebt' && value !== true) {
      super.onFieldChange('totalDebtPayoff', null);
    }

    if (field == 'loanPurpose') {
      if (value == PURCHASE) {
        super.onFieldChange('cashOut', null);
        super.onFieldChange('anyDebt', null);
        super.onFieldChange('capitalImprovements', '');
        super.onFieldChange('lastPurchaseDate', '');
        super.onFieldChange('totalDebtPayoff', '');
      }
    }
  }

  getLoanPortfolioInformation() {
    const formValues = this.getFormValues();
    const isRefinance = formValues.loanPurpose === REFINANCE;

    return {
      propertyState: formValues.propertyStates,
      propertyCounties: formValues.propertyCounties?.map(county => county.value) || [],
      loanPurpose: formValues.loanPurpose,
      loanRecourse: formValues.loanRecourse,
      aggregates: {
        totalDebtPayoff: isRefinance && formValues.anyDebt ? formValues.totalDebtPayoff : null,
        acquisitionPrice: formValues.acquisitionPrice,
        maximumAsIsValue: formValues.maximumAsIsValue,
        minimumAsIsValue: formValues.minimumAsIsValue,
        capitalImprovements: formValues.capitalImprovements,
        totalGrossMonthlyRent: formValues.totalGrossMonthlyRent,
        totalGrossAnnualTaxes: formValues.totalGrossAnnualTaxes,
        totalGrossAnnualInsurance: formValues.totalGrossAnnualInsurance,
        totalAnnualHOADues: formValues.totalAnnualHOADues,
      },
      flags: {
        section8: formValues.section8,
        anyDebt: isRefinance ? formValues.anyDebt : null,
        cashOut: isRefinance && formValues.anyDebt ? formValues.cashOut : null,
      },
      properties: this.getProperties(),
      dates: {
        lastPurchaseDate: isRefinance ? formatDate(formValues.lastPurchaseDate, DateFormat.MMDDYYYY) : null
      }
    };
  }

  loadPortfolioInformation(quoteData) {
    const portfolioData = quoteData.portfolioInformation || {};
    this.loadForm({
      propertyStates: portfolioData.propertyState,
      propertyCounties: portfolioData.propertyCounties?.map(county => ({ value: county, label: county })) || [],
      loanPurpose: portfolioData.loanPurpose,
      loanRecourse: portfolioData.loanRecourse,

      totalDebtPayoff: portfolioData.aggregates?.totalDebtPayoff,
      acquisitionPrice: portfolioData.aggregates?.acquisitionPrice,
      maximumAsIsValue: portfolioData.aggregates?.maximumAsIsValue,
      minimumAsIsValue: portfolioData.aggregates?.minimumAsIsValue,
      capitalImprovements: portfolioData.aggregates?.capitalImprovements,
      totalGrossMonthlyRent: portfolioData.aggregates?.totalGrossMonthlyRent,
      totalGrossAnnualTaxes: portfolioData.aggregates?.totalGrossAnnualTaxes,
      totalGrossAnnualInsurance: portfolioData.aggregates?.totalGrossAnnualInsurance,
      totalAnnualHOADues: portfolioData.aggregates?.totalAnnualHOADues,

      section8: portfolioData.flags?.section8,
      anyDebt: portfolioData.flags?.anyDebt,
      cashOut: portfolioData.flags?.cashOut,

      lastPurchaseDate: portfolioData.dates?.lastPurchaseDate,
    });

    portfolioData.properties?.forEach(property => {
      const propertyTypeFormStore = new RentalPropertyTypeFormStore(this.globalStore);
      propertyTypeFormStore.loadForm(property);
      this.propertyTypeFormStores.push(propertyTypeFormStore);
    });
  }

  get rentalPortfoliosPropertyTypesAsString() {
    return this.getProperties()
      .filter(x => x.propertyType)
      .map(x => x.propertyType)
      .join(',');
  };


}